/* bareposter theme.js - progressive enhancement only. Without JS the cart icon
   links to /cart, the product form posts, and variant pickers use the noscript selects. */

(() => {
  const root = document.documentElement;
  root.classList.add('js');

  const routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const overlay = document.querySelector('[data-overlay]');
  const live = document.querySelector('[data-live-region]');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let lastFocus = null;
  let openDrawer = null;

  const announce = (message) => {
    if (live) live.textContent = message;
  };

  /* Drawers */

  const closeDrawer = () => {
    if (!openDrawer) return;
    openDrawer.removeAttribute('data-open');
    document.querySelectorAll('[data-drawer-open][aria-expanded]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (overlay) overlay.removeAttribute('data-open');
    document.body.classList.remove('overflow-hidden');
    openDrawer = null;
    if (lastFocus) lastFocus.focus();
  };

  const showDrawer = (id, trigger) => {
    const drawer = document.getElementById(id);
    if (!drawer) return false;
    lastFocus = trigger || document.activeElement;
    openDrawer = drawer;
    drawer.setAttribute('data-open', 'true');
    if (overlay) overlay.setAttribute('data-open', 'true');
    document.body.classList.add('overflow-hidden');
    if (trigger && trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
    const first = drawer.querySelector('[data-drawer-close]') || drawer.querySelector(FOCUSABLE);
    if (first) first.focus();
    return true;
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      if (showDrawer(opener.getAttribute('data-drawer-open'), opener)) event.preventDefault();
      return;
    }
    if (event.target.closest('[data-drawer-close]') || event.target.closest('[data-overlay]')) {
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawer();
      document.querySelectorAll('[data-dropdown-toggle][aria-expanded="true"]').forEach(closeDropdown);
      return;
    }
    if (event.key !== 'Tab' || !openDrawer) return;
    const items = [...openDrawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Header dropdowns */

  const closeDropdown = (toggle) => toggle.setAttribute('aria-expanded', 'false');

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-dropdown-toggle]');
    document.querySelectorAll('[data-dropdown-toggle][aria-expanded="true"]').forEach((open) => {
      if (open !== toggle) closeDropdown(open);
    });
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', String(toggle.getAttribute('aria-expanded') !== 'true'));
  });

  // Hover opens on pointer devices, focus opens for keyboard, touch falls back to tap.
  const hoverable = window.matchMedia('(pointer: fine)').matches;
  document.querySelectorAll('.header__item').forEach((item) => {
    const toggle = item.querySelector('[data-dropdown-toggle]');
    if (!toggle) return;
    const set = (open) => toggle.setAttribute('aria-expanded', String(open));
    if (hoverable) {
      item.addEventListener('mouseenter', () => set(true));
      item.addEventListener('mouseleave', () => set(false));
    }
    item.addEventListener('focusin', () => set(true));
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) set(false);
    });
  });

  /* Cart */

  const refreshCart = (html) => {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('[data-cart-drawer]');
    const current = document.querySelector('[data-cart-drawer]');
    if (!next || !current) return;
    const wasOpen = current.hasAttribute('data-open');
    current.replaceWith(next);
    if (wasOpen) {
      next.setAttribute('data-open', 'true');
      openDrawer = next;
    }
    const count = next.getAttribute('data-cart-count') || '0';
    document.querySelectorAll('[data-cart-count-bubble]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.hidden = count === '0';
    });
  };

  const onCartPage = () => document.body.classList.contains('template-cart');

  const applyCart = (data) => {
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    refreshCart(data.sections && data.sections['cart-drawer']);
  };

  const postCart = (url, body) =>
    fetch(`${routes}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer' }),
    }).then((response) => response.json());

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-change]');
    const set = event.target.closest('[data-qty-set]');
    const control = step || set;
    if (!control) return;

    const line = control.getAttribute('data-line');
    let quantity = 0;
    if (step) {
      const input = document.querySelector(`[data-qty-input][data-line="${line}"]`);
      quantity = Math.max(0, Number(input ? input.value : 1) + Number(step.getAttribute('data-qty-change')));
    }
    event.preventDefault();
    postCart('cart/change.js', { line: Number(line), quantity })
      .then(applyCart)
      .catch(() => window.location.assign(`${routes}cart`));
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-qty-input]');
    if (!input) return;
    postCart('cart/change.js', { line: Number(input.getAttribute('data-line')), quantity: Math.max(0, Number(input.value)) })
      .then(applyCart)
      .catch(() => window.location.assign(`${routes}cart`));
  });

  /* Add to cart */

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[data-add-button]');
    const label = button ? button.textContent : '';
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.textContent = button.getAttribute('data-adding-text') || label;
    }

    const payload = new FormData(form);
    payload.append('sections', 'cart-drawer');

    fetch(`${routes}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: payload,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) throw new Error(data.description || data.message);
        refreshCart(data.sections && data.sections['cart-drawer']);
        announce(form.getAttribute('data-added-text') || '');
        showDrawer('CartDrawer', button);
      })
      .catch(() => form.submit())
      .finally(() => {
        if (button) {
          button.removeAttribute('aria-disabled');
          button.textContent = label;
        }
      });
  });

  /* Variant picker */

  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    const dataEl = document.getElementById(picker.getAttribute('data-variant-data'));
    if (!dataEl) return;
    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    picker.addEventListener('change', () => {
      const chosen = [...picker.querySelectorAll('input:checked, select')].map((input) => input.value);
      const match = variants.find((variant) => variant.options.every((option, index) => option === chosen[index]));
      const idInput = document.querySelector('[data-variant-id]');
      const priceEl = document.querySelector('[data-variant-price]');
      const button = document.querySelector('[data-add-button]');

      if (idInput) idInput.value = match ? match.id : '';
      if (priceEl && match) priceEl.innerHTML = match.price_html;
      if (button) {
        const available = Boolean(match && match.available);
        button.disabled = !available;
        button.textContent = available
          ? button.getAttribute('data-available-text')
          : button.getAttribute('data-unavailable-text');
      }
      if (match && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }
    });
  });

  /* Motion toggle (marquee) */

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-motion-toggle]');
    if (!toggle) return;
    const region = toggle.closest('[data-marquee]');
    if (!region) return;
    const paused = region.getAttribute('data-paused') === 'true';
    region.setAttribute('data-paused', String(!paused));
    toggle.setAttribute('aria-pressed', String(!paused));
    toggle.textContent = paused ? toggle.getAttribute('data-pause-text') : toggle.getAttribute('data-play-text');
  });

  /* Collection sort */

  const sort = document.querySelector('[data-sort]');
  if (sort) sort.addEventListener('change', () => sort.form.submit());
})();
